import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import accessibility from 'highcharts/modules/accessibility';

accessibility(Highcharts);

interface SqlResultChartProps {
  columns: string[];
  rows: string[][];
  height?: number;
}

interface DerivedChartData {
  categories: string[];
  chartType: 'line' | 'bar' | 'column';
  series: Highcharts.SeriesOptionsType[];
  legendEnabled: boolean;
  xAxisTitle?: string;
}

const DATE_PATTERNS = [
  /^\d{4}-\d{2}-\d{2}/, // 2025-01-01
  /^\d{4}-\d{2}$/, // 2025-01
  /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i, // Month names
  /^\d{1,2}\/\d{1,2}\/\d{2,4}$/, // MM/DD/YYYY
];

const isDateLike = (value: string): boolean => DATE_PATTERNS.some((p) => p.test(value.trim()));

const isNumeric = (value: string): boolean => {
  if (!value || value.trim() === '') return false;
  const cleaned = value.replace(/[$,%]/g, '').trim();
  return !isNaN(Number(cleaned)) && cleaned !== '';
};

const parseNumeric = (value: string): number => {
  const cleaned = value.replace(/[$,%]/g, '').trim();
  return Number(cleaned) || 0;
};

const detectChartType = (columns: string[], rows: string[][]): 'line' | 'bar' | 'column' => {
  if (rows.length === 0 || columns.length < 2) return 'column';

  const firstColValues = rows.map((r) => r[0] ?? '');
  const hasDateCategory = firstColValues.some(isDateLike);

  if (hasDateCategory) return 'line';
  if (rows.length > 10) return 'bar';
  return 'column';
};

const formatSeriesName = (name: string): string => name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const DATE_COLUMN_HINTS = ['date', 'time', 'timestamp', 'created', 'updated'];

const normalizeDateBucket = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const isoDateMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDateMatch) {
    return isoDateMatch[1];
  }

  if (/^\d{4}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const parsedTime = Date.parse(trimmed);
  if (!Number.isNaN(parsedTime)) {
    return new Date(parsedTime).toISOString().slice(0, 10);
  }

  return null;
};

const findDateColumnIndex = (columns: string[], rows: string[][]): number => {
  const candidateIndices = columns
    .map((_, index) => index)
    .filter((index) => rows.some((row) => isDateLike(row[index] ?? '')));

  if (candidateIndices.length === 0) {
    return -1;
  }

  const hintedIndex = candidateIndices.find((index) => {
    const normalized = columns[index].toLowerCase();
    return DATE_COLUMN_HINTS.some((hint) => normalized.includes(hint));
  });

  return hintedIndex ?? candidateIndices[0];
};

const buildDateCountFallback = (columns: string[], rows: string[][]): DerivedChartData | null => {
  const dateColumnIndex = findDateColumnIndex(columns, rows);
  if (dateColumnIndex === -1) {
    return null;
  }

  const bucketCounts = new Map<string, number>();

  rows.forEach((row) => {
    const rawValue = row[dateColumnIndex] ?? '';
    const bucket = normalizeDateBucket(rawValue);
    if (!bucket) {
      return;
    }

    bucketCounts.set(bucket, (bucketCounts.get(bucket) ?? 0) + 1);
  });

  const categories = Array.from(bucketCounts.keys()).sort((a, b) => a.localeCompare(b));
  if (categories.length === 0) {
    return null;
  }

  return {
    categories,
    chartType: 'line',
    series: [
      {
        type: 'line',
        name: 'Row Count',
        data: categories.map((category) => bucketCounts.get(category) ?? 0),
      },
    ],
    legendEnabled: false,
    xAxisTitle: formatSeriesName(columns[dateColumnIndex]),
  };
};

export const buildSqlChartOptions = (
  columns: string[],
  rows: string[][],
  height: number,
): Highcharts.Options | null => {
  if (columns.length === 0 || rows.length === 0) {
    return null;
  }

  const numericColIndices = columns
    .map((_, i) => i)
    .filter((i) => i > 0 && rows.some((row) => isNumeric(row[i] ?? '')));

  let derivedChartData: DerivedChartData | null = null;

  if (numericColIndices.length > 0) {
    const categories = rows.map((r) => r[0] ?? '');
    const chartType = detectChartType(columns, rows);

    derivedChartData = {
      categories,
      chartType,
      series: numericColIndices.map((colIdx) => ({
        type: chartType,
        name: formatSeriesName(columns[colIdx]),
        data: rows.map((row) => parseNumeric(row[colIdx] ?? '0')),
      })),
      legendEnabled: numericColIndices.length > 1,
    };
  } else {
    derivedChartData = buildDateCountFallback(columns, rows);
  }

  if (!derivedChartData) {
    return null;
  }

  return {
    chart: {
      type: derivedChartData.chartType,
      height,
      style: { fontFamily: 'inherit' },
      backgroundColor: 'transparent',
      spacing: [10, 10, 10, 10],
    },
    title: { text: undefined },
    credits: { enabled: false },
    xAxis: {
      categories: derivedChartData.categories,
      title: derivedChartData.xAxisTitle ? { text: derivedChartData.xAxisTitle } : undefined,
      labels: {
        style: { fontSize: '10px', color: '#6b7280' },
        rotation: derivedChartData.categories.length > 8 ? -45 : 0,
      },
      lineColor: '#e5e7eb',
      tickColor: '#e5e7eb',
    },
    yAxis: {
      title: { text: undefined },
      labels: { style: { fontSize: '10px', color: '#6b7280' } },
      gridLineColor: '#f3f4f6',
    },
    legend: {
      enabled: derivedChartData.legendEnabled,
      itemStyle: { fontSize: '11px', fontWeight: '400', color: '#374151' },
    },
    tooltip: {
      shared: true,
      backgroundColor: '#1f2937',
      borderColor: '#374151',
      style: { color: '#f9fafb', fontSize: '11px' },
      borderRadius: 6,
    },
    plotOptions: {
      series: {
        animation: { duration: 400 },
      },
      column: {
        borderRadius: 3,
        borderWidth: 0,
      },
      bar: {
        borderRadius: 3,
        borderWidth: 0,
      },
      line: {
        marker: { radius: 3 },
        lineWidth: 2,
      },
    },
    colors: ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'],
    series: derivedChartData.series,
  };
};

/**
 * Auto-charting component for SQL result data.
 * Detects chart type from data shape and renders via Highcharts.
 */
export const SqlResultChart = ({ columns, rows, height = 280 }: SqlResultChartProps) => {
  const chartOptions = useMemo(
    (): Highcharts.Options | null => buildSqlChartOptions(columns, rows, height),
    [columns, rows, height],
  );

  if (!chartOptions) {
    return <div className="sql-chart-empty">Unable to chart this data — no numeric or date-like columns detected.</div>;
  }

  return (
    <div className="sql-chart-container">
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </div>
  );
};
